import { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { placeMatkaBet } from "../../../api/services/matka";
import { setPlayerBalance } from "../../../features/slices/UserSlice";
import MatkaBetReviewModal from "./MatkaBetReviewModal";
import MatkaPaymentSuccessModal from "./MatkaPaymentSuccessModal";
import { buildMatkaBetDraft } from "./matkaBetPayload";

function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `bet-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function betErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof data?.error === "string" && data.error.trim()) return data.error;
  if (Array.isArray(data?.errors) && data.errors.length) {
    const first = data.errors[0];
    if (typeof first === "string") return first;
    if (typeof first?.message === "string") return first.message;
  }
  if (error?.response?.status === 409 || data?.code === "DUPLICATE_BET") {
    return "This bet was already placed.";
  }
  if (typeof error?.message === "string" && error.message && !error.message.startsWith("Request failed")) {
    return error.message;
  }
  return "Unable to place the bet. Please try again.";
}

export function useMatkaBetPlacement({
  market,
  gameType,
  session,
  bids,
  digitPoints,
  onPlaced,
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const idempotencyKey = useRef("");

  const draft = useMemo(
    () => buildMatkaBetDraft({ gameType, session, bids, digitPoints }),
    [gameType, session, bids, digitPoints]
  );

  const totalPoints = draft.rows.reduce((sum, row) => sum + row.points, 0);
  const canSubmit = Boolean(market?.marketId) && draft.selections.length > 0 && Boolean(draft.betTypeCode);

  const openReview = () => {
    if (!draft.selections.length) return;
    idempotencyKey.current = createIdempotencyKey();
    setError(market?.marketId ? "" : "This market is not open for betting.");
    setOpen(true);
  };

  const closeReview = () => {
    if (submitting) return;
    setOpen(false);
    setError("");
  };

  const confirmBet = async () => {
    if (submitting || !canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const placed = await placeMatkaBet({
        marketId: market.marketId,
        betTypeCode: draft.betTypeCode,
        session: draft.session,
        selections: draft.selections,
        idempotencyKey: idempotencyKey.current,
      });
      const amount = Number(placed.total_amount) || totalPoints;
      const oldBalance = Number(placed.balance?.before);
      const newBalance = Number(placed.balance?.after);
      if (Number.isFinite(newBalance)) {
        dispatch(setPlayerBalance(newBalance));
      }
      setOpen(false);
      setSuccess({
        amount,
        oldBalance: Number.isFinite(oldBalance) ? oldBalance : 0,
        newBalance: Number.isFinite(newBalance) ? newBalance : 0,
      });
      if (onPlaced) onPlaced();
    } catch (requestError) {
      setError(betErrorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  };

  const betModals = (
    <>
      <MatkaBetReviewModal
        show={open}
        title={gameType?.title || "Confirm Bet"}
        rows={draft.rows}
        totalPoints={totalPoints}
        error={error}
        submitting={submitting}
        canSubmit={canSubmit}
        onClose={closeReview}
        onConfirm={confirmBet}
      />
      <MatkaPaymentSuccessModal
        show={Boolean(success)}
        onClose={() => setSuccess(null)}
        amount={success?.amount || 0}
        oldBalance={success?.oldBalance || 0}
        newBalance={success?.newBalance || 0}
      />
    </>
  );

  return { openReview, betModals };
}
